import React, { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { logger } from '@/utils/logger'

interface AnnotationOverlayProps {
  viewer: any
  tool: string | null
  onAnnotationsChange?: (annotations: any[]) => void
  onClearAll?: () => void
}

const AnnotationOverlay: React.FC<AnnotationOverlayProps> = ({ viewer, tool, onAnnotationsChange }) => {
  const svgRef = useRef<SVGSVGElement>(null)
  const [annotations, setAnnotations] = useState<any[]>([])
  const [isDrawing, setIsDrawing] = useState(false)
  const startPointRef = useRef<{ x: number; y: number } | null>(null)
  const startViewportPointRef = useRef<any>(null)
  const tempShapeRef = useRef<SVGElement | null>(null)
  const pathPointsRef = useRef<string>('')
  const [showTextInput, setShowTextInput] = useState(false)
  const [textPosition, setTextPosition] = useState({ x: 0, y: 0 })
  const [textValue, setTextValue] = useState('')
  const [showNoteForm, setShowNoteForm] = useState(false)
  const [noteFormPosition, setNoteFormPosition] = useState({ x: 0, y: 0 })
  const [noteTitle, setNoteTitle] = useState('')
  const [noteDescription, setNoteDescription] = useState('')
  const pendingAnnotationRef = useRef<any>(null)

  const overlayRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!overlayRef.current || !svgRef.current || !tool) return

    const overlay = overlayRef.current
    const svg = svgRef.current

    const handleMouseDown = (e: MouseEvent) => {
      const rect = svg.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top

      startPointRef.current = { x, y }

      // Convert screen coordinates to image pixel coordinates (KFBio approach)
      if (viewer && window.OpenSeadragon) {
        // Get canvas position
        const canvasRect = viewer.canvas.getBoundingClientRect()
        const canvasX = e.clientX - canvasRect.left
        const canvasY = e.clientY - canvasRect.top

        // Convert to viewport point
        const webPoint = new window.OpenSeadragon.Point(canvasX, canvasY)
        const viewportPoint = viewer.viewport.pointFromPixel(webPoint)

        // Convert viewport to IMAGE PIXEL coordinates (not normalized)
        const imagePoint = viewer.viewport.viewportToImageCoordinates(viewportPoint)

        // Store actual image pixel coordinates
        startViewportPointRef.current = {
          x: imagePoint.x,
          y: imagePoint.y
        }
      }

      // Text tool - show input dialog
      if (tool === 'text') {
        setTextPosition({ x, y })
        setShowTextInput(true)
        return
      }

      // Pen tool - start path
      if (tool === 'pen') {
        pathPointsRef.current = `M ${x} ${y}`
      }

      setIsDrawing(true)
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDrawing || !startPointRef.current) return

      const rect = svg.getBoundingClientRect()
      const currentPoint = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      }

      // Remove temp shape
      if (tempShapeRef.current && svg.contains(tempShapeRef.current)) {
        svg.removeChild(tempShapeRef.current)
      }

      // Create new temp shape
      const startPoint = startPointRef.current

      if (tool === 'pen') {
        // Pen tool - add point to path
        pathPointsRef.current += ` L ${currentPoint.x} ${currentPoint.y}`
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
        path.setAttribute('d', pathPointsRef.current)
        path.setAttribute('stroke', '#00ff00')
        path.setAttribute('stroke-width', '3')
        path.setAttribute('fill', 'none')
        path.setAttribute('stroke-linecap', 'round')
        path.setAttribute('stroke-linejoin', 'round')
        svg.appendChild(path)
        tempShapeRef.current = path
      } else if (tool === 'rectangle') {
        const width = currentPoint.x - startPoint.x
        const height = currentPoint.y - startPoint.y
        const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
        rect.setAttribute('x', String(Math.min(startPoint.x, currentPoint.x)))
        rect.setAttribute('y', String(Math.min(startPoint.y, currentPoint.y)))
        rect.setAttribute('width', String(Math.abs(width)))
        rect.setAttribute('height', String(Math.abs(height)))
        rect.setAttribute('stroke', '#00ff00')
        rect.setAttribute('stroke-width', '3')
        rect.setAttribute('fill', 'rgba(0, 255, 0, 0.2)')
        svg.appendChild(rect)
        tempShapeRef.current = rect
      } else if (tool === 'circle') {
        const dx = currentPoint.x - startPoint.x
        const dy = currentPoint.y - startPoint.y
        const radius = Math.sqrt(dx * dx + dy * dy)
        const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
        circle.setAttribute('cx', String(startPoint.x))
        circle.setAttribute('cy', String(startPoint.y))
        circle.setAttribute('r', String(radius))
        circle.setAttribute('stroke', '#00ff00')
        circle.setAttribute('stroke-width', '3')
        circle.setAttribute('fill', 'rgba(0, 255, 0, 0.2)')
        svg.appendChild(circle)
        tempShapeRef.current = circle
      } else if (tool === 'arrow') {
        const line = document.createElementNS('http://www.w3.org/2000/svg', 'line')
        line.setAttribute('x1', String(startPoint.x))
        line.setAttribute('y1', String(startPoint.y))
        line.setAttribute('x2', String(currentPoint.x))
        line.setAttribute('y2', String(currentPoint.y))
        line.setAttribute('stroke', '#00ff00')
        line.setAttribute('stroke-width', '3')
        line.setAttribute('marker-end', 'url(#arrowhead)')
        svg.appendChild(line)
        tempShapeRef.current = line
      } else if (tool === 'ellipse') {
        const rx = Math.abs(currentPoint.x - startPoint.x) / 2
        const ry = Math.abs(currentPoint.y - startPoint.y) / 2
        const cx = (startPoint.x + currentPoint.x) / 2
        const cy = (startPoint.y + currentPoint.y) / 2
        const ellipse = document.createElementNS('http://www.w3.org/2000/svg', 'ellipse')
        ellipse.setAttribute('cx', String(cx))
        ellipse.setAttribute('cy', String(cy))
        ellipse.setAttribute('rx', String(rx))
        ellipse.setAttribute('ry', String(ry))
        ellipse.setAttribute('stroke', '#00ff00')
        ellipse.setAttribute('stroke-width', '3')
        ellipse.setAttribute('fill', 'rgba(0, 255, 0, 0.2)')
        svg.appendChild(ellipse)
        tempShapeRef.current = ellipse
      }
    }

    const handleMouseUp = () => {
      if (tempShapeRef.current && tool !== 'text' && viewer && window.OpenSeadragon) {
        const svgRect = svg.getBoundingClientRect()
        const canvasRect = viewer.canvas.getBoundingClientRect()

        // Helper function to convert SVG coordinates to image pixel coordinates (KFBio approach)
        const svgToImagePixels = (svgX: number, svgY: number) => {
          // SVG coordinates are relative to SVG element
          const clientX = svgRect.left + svgX
          const clientY = svgRect.top + svgY

          // Convert to canvas coordinates
          const canvasX = clientX - canvasRect.left
          const canvasY = clientY - canvasRect.top

          // Convert to viewport coordinates
          const webPoint = new window.OpenSeadragon.Point(canvasX, canvasY)
          const viewportPoint = viewer.viewport.pointFromPixel(webPoint)

          // Convert to IMAGE PIXEL coordinates (not normalized)
          const imagePoint = viewer.viewport.viewportToImageCoordinates(viewportPoint)

          return {
            x: imagePoint.x,
            y: imagePoint.y
          }
        }

        // Extract shape data and convert all coordinates to IMAGE PIXEL coordinates
        const element = tempShapeRef.current
        let shapeData: any = { type: tool }

        if (tool === 'arrow' || tool === 'line') {
          const x1 = parseFloat(element.getAttribute('x1') || '0')
          const y1 = parseFloat(element.getAttribute('y1') || '0')
          const x2 = parseFloat(element.getAttribute('x2') || '0')
          const y2 = parseFloat(element.getAttribute('y2') || '0')

          const p1 = svgToImagePixels(x1, y1)
          const p2 = svgToImagePixels(x2, y2)

          shapeData = {
            type: tool,
            x1: p1.x,
            y1: p1.y,
            x2: p2.x,
            y2: p2.y
          }
        } else if (tool === 'rectangle') {
          const x = parseFloat(element.getAttribute('x') || '0')
          const y = parseFloat(element.getAttribute('y') || '0')
          const width = parseFloat(element.getAttribute('width') || '0')
          const height = parseFloat(element.getAttribute('height') || '0')

          const p1 = svgToImagePixels(x, y)
          const p2 = svgToImagePixels(x + width, y + height)

          shapeData = {
            type: tool,
            x: p1.x,
            y: p1.y,
            width: p2.x - p1.x,
            height: p2.y - p1.y
          }
        } else if (tool === 'circle') {
          const cx = parseFloat(element.getAttribute('cx') || '0')
          const cy = parseFloat(element.getAttribute('cy') || '0')
          const r = parseFloat(element.getAttribute('r') || '0')

          const center = svgToImagePixels(cx, cy)
          const edge = svgToImagePixels(cx + r, cy)

          shapeData = {
            type: tool,
            cx: center.x,
            cy: center.y,
            r: edge.x - center.x
          }
        } else if (tool === 'ellipse') {
          const cx = parseFloat(element.getAttribute('cx') || '0')
          const cy = parseFloat(element.getAttribute('cy') || '0')
          const rx = parseFloat(element.getAttribute('rx') || '0')
          const ry = parseFloat(element.getAttribute('ry') || '0')

          const center = svgToImagePixels(cx, cy)
          const rxPoint = svgToImagePixels(cx + rx, cy)
          const ryPoint = svgToImagePixels(cx, cy + ry)

          shapeData = {
            type: tool,
            cx: center.x,
            cy: center.y,
            rx: rxPoint.x - center.x,
            ry: ryPoint.y - center.y
          }
        } else if (tool === 'pen') {
          // For pen tool, convert path points to viewport coordinates
          const pathData = element.getAttribute('d') || ''
          shapeData = {
            type: tool,
            pathData: pathData,
            startViewport: startViewportPointRef.current
          }
        }

        // Store pending annotation with shape data
        logger.debug('📍 Saving annotation with IMAGE PIXEL coordinates:', shapeData)
        logger.debug('📐 Current zoom:', viewer.viewport.getZoom())

        pendingAnnotationRef.current = {
          type: tool,
          svg: tempShapeRef.current.outerHTML,
          timestamp: Date.now(),
          shapeData: shapeData,
          viewportPoint: startViewportPointRef.current ? {
            x: startViewportPointRef.current.x,
            y: startViewportPointRef.current.y
          } : null
        }

        // Position form near the end point
        const formRect = svgRef.current?.getBoundingClientRect()
        if (formRect && startPointRef.current) {
          setNoteFormPosition({
            x: Math.min(startPointRef.current.x + 20, formRect.width - 320),
            y: Math.min(startPointRef.current.y, formRect.height - 200)
          })
        }

        setShowNoteForm(true)
      }
      setIsDrawing(false)
      startPointRef.current = null
      startViewportPointRef.current = null
      tempShapeRef.current = null
      pathPointsRef.current = ''
    }

    overlay.addEventListener('mousedown', handleMouseDown)
    overlay.addEventListener('mousemove', handleMouseMove)
    overlay.addEventListener('mouseup', handleMouseUp)

    return () => {
      overlay.removeEventListener('mousedown', handleMouseDown)
      overlay.removeEventListener('mousemove', handleMouseMove)
      overlay.removeEventListener('mouseup', handleMouseUp)
    }
  }, [tool, isDrawing, annotations, onAnnotationsChange])

  const handleTextSubmit = () => {
    if (!textValue.trim()) {
      setShowTextInput(false)
      setTextValue('')
      return
    }

    const textElement = document.createElementNS('http://www.w3.org/2000/svg', 'text')
    textElement.setAttribute('x', String(textPosition.x))
    textElement.setAttribute('y', String(textPosition.y))
    textElement.setAttribute('fill', '#00ff00')
    textElement.setAttribute('font-size', '16')
    textElement.setAttribute('font-weight', 'bold')
    textElement.setAttribute('stroke', '#000')
    textElement.setAttribute('stroke-width', '0.5')
    textElement.textContent = textValue

    const newAnnotation = {
      type: 'text',
      svg: textElement.outerHTML,
      timestamp: Date.now(),
      text: textValue,
      title: textValue,
      description: ''
    }

    const updated = [...annotations, newAnnotation]
    setAnnotations(updated)
    if (onAnnotationsChange) {
      onAnnotationsChange(updated)
    }

    toast.success('Text annotation added')
    setShowTextInput(false)
    setTextValue('')
  }

  const handleNoteSubmit = () => {
    if (!pendingAnnotationRef.current) return

    const newAnnotation = {
      ...pendingAnnotationRef.current,
      title: noteTitle.trim() || `${pendingAnnotationRef.current.type} annotation`,
      description: noteDescription.trim()
    }

    const updated = [...annotations, newAnnotation]
    setAnnotations(updated)
    if (onAnnotationsChange) {
      onAnnotationsChange(updated)
    }

    toast.success(`${newAnnotation.type.charAt(0).toUpperCase() + newAnnotation.type.slice(1)} annotation added`)

    // Reset form
    setShowNoteForm(false)
    setNoteTitle('')
    setNoteDescription('')
    pendingAnnotationRef.current = null
  }

  const handleNoteCancel = () => {
    // Remove the temporary shape from SVG
    if (pendingAnnotationRef.current && svgRef.current) {
      const tempElements = svgRef.current.querySelectorAll('[stroke="#00ff00"]')
      if (tempElements.length > 0) {
        const lastElement = tempElements[tempElements.length - 1]
        lastElement.remove()
      }
    }

    setShowNoteForm(false)
    setNoteTitle('')
    setNoteDescription('')
    pendingAnnotationRef.current = null
  }

  if (!tool && !showTextInput && !showNoteForm) return null

  return (
    <>
      {/* Transparent overlay for mouse events - doesn't capture wheel events for zoom */}
      {tool && (
        <div
          ref={overlayRef}
          className="absolute top-0 left-0 w-full h-full"
          style={{
            zIndex: 9,
            cursor: 'crosshair',
            pointerEvents: 'auto'  // Only capture events when tool is active
          }}
        />
      )}
      <svg
        ref={svgRef}
        className="absolute top-0 left-0 w-full h-full"
        style={{
          zIndex: 10,
          pointerEvents: 'none',  // Don't capture pointer events - let them pass to OpenSeadragon
          cursor: tool ? 'crosshair' : 'default'
        }}
      >
        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="10"
            refX="9"
            refY="3"
            orient="auto"
          >
            <polygon points="0 0, 10 3, 0 6" fill="#00ff00" />
          </marker>
        </defs>

        {/* Render saved annotations */}
        {annotations.map((ann, idx) => (
          <g key={idx} dangerouslySetInnerHTML={{ __html: ann.svg }} />
        ))}
      </svg>

      {/* Text input dialog */}
      {showTextInput && (
        <div
          className="absolute bg-white rounded-lg shadow-xl p-4 z-50"
          style={{
            left: `${textPosition.x}px`,
            top: `${textPosition.y}px`,
            minWidth: '250px'
          }}
        >
          <div className="mb-3">
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Add Text Annotation
            </label>
            <input
              type="text"
              value={textValue}
              onChange={(e) => setTextValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleTextSubmit()
                if (e.key === 'Escape') {
                  setShowTextInput(false)
                  setTextValue('')
                }
              }}
              placeholder="Enter annotation text..."
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
              autoFocus
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleTextSubmit}
              className="flex-1 px-3 py-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm font-medium"
            >
              Add
            </button>
            <button
              onClick={() => {
                setShowTextInput(false)
                setTextValue('')
              }}
              className="flex-1 px-3 py-1.5 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 text-sm font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Note form dialog */}
      {showNoteForm && (
        <div
          className="absolute bg-white rounded-lg shadow-2xl p-4 z-50 border-2 border-blue-500"
          style={{
            left: `${noteFormPosition.x}px`,
            top: `${noteFormPosition.y}px`,
            minWidth: '300px',
            maxWidth: '400px'
          }}
        >
          <h3 className="text-lg font-bold text-gray-800 mb-3">Annotation Details</h3>

          <div className="mb-3">
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={noteTitle}
              onChange={(e) => setNoteTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && noteTitle.trim()) handleNoteSubmit()
                if (e.key === 'Escape') handleNoteCancel()
              }}
              placeholder="Enter title for this annotation..."
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
              autoFocus
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={noteDescription}
              onChange={(e) => setNoteDescription(e.target.value)}
              placeholder="Enter description (optional)..."
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500 resize-none"
              rows={3}
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleNoteSubmit}
              className="flex-1 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 text-sm font-bold"
            >
              Save Note
            </button>
            <button
              onClick={handleNoteCancel}
              className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 text-sm font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  )
}

export default AnnotationOverlay
